import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';
import {
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@mui/material';
import html2canvas from 'html2canvas';

import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import type { ActionPlan } from 'src/types/planAction';
import delay from 'src/helper/delay';
import { downloadActionPlanPPT } from 'src/helper/pptgen';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService, { HttpServiceInstance } from 'src/services/api/http';
import { useUserContext } from 'src/components/app/context/UserContext';
import { getParentOrgUnit } from 'src/components/common/OrgTree/helper';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import KPIHistory from 'src/components/planAction/PlanActionDetailPage/components/Action/components/KPISelector/components/KPIHistory';
import TargetStatementResults from 'src/components/planAction/PlanActionDetailPage/components/Action/components/KPISelector/components/TargetStatementResults';
import PPTModal from 'src/components/planAction/PlanActionStatisticsPage/components/PPTModal';

import LanguageChooserModal from './LanguageChooserModal';
import PlanActionTableHead from './TableHead';
import PlanActionTableRow from './TableRow';

const PlanActionTable = () => {
  const { t } = useTranslation('planAction');
  const {
    selectedOrgUnit,
    actionPlans,
    loadingActionPlans,
    planActionStatus,
    orgTreeState: planActionOrgTeeState,
  } = usePlanActionContext();
  const { currentSurvey } = useUserContext();
  const [selectedActionPlanForDownload, setSelectedActionPlanForDownload] =
    useState(null);
  const [selectedActionPlanForPPT, setSelectedActionPlanForPPT] =
    useState<ActionPlan | null>();

  const [childOrgUnits, setChildOrgUnits] = useState<
    ModuleOrPlanActionOrgUnit[]
  >([]);

  const downloadPrintedActionPlan = useCallback(
    (language: string) =>
      HttpServiceInstance.getFile(
        `${apiPaths.PLAN_ACTION.ACTION_PLAN}${selectedActionPlanForDownload}/download_pdf/`,
        { lang: language },
        true,
      ),
    [selectedActionPlanForDownload],
  );

  const downloadPPTActionPlan = async (id: number) => {
    const w = '1920px';
    const bodyCopy = document.body;
    bodyCopy.style.width = w;

    const selectedActionPlan = actionPlans.find(
      (actionPlan) => actionPlan.id === id,
    );

    setSelectedActionPlanForPPT(selectedActionPlan);

    await delay(1000);

    // collect graphs and assign them to their action ids
    const actionGraphs = selectedActionPlan?.actions.reduce(
      (acc: { [key: number]: HTMLElement }, action) => {
        const element = document.getElementById(`kpi-graph-${action.id}`);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (element) return { ...acc, [action.id!]: element };
        return acc;
      },
      {},
    );

    interface ActionGraphsCanvas {
      [key: number]: {
        dataUrl: string;
        height: number;
        width: number;
      };
    }

    // create an object with the action id as key to identify the images later
    const actionGraphsImg: ActionGraphsCanvas = {};
    if (actionGraphs) {
      if (actionGraphs) {
        for (const [key, graph] of Object.entries(actionGraphs)) {
          const canvasElement = await html2canvas(graph);
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const canvas = canvasElement?.getContext('2d')!.canvas;

          Object.assign(actionGraphsImg, {
            [key]: {
              dataUrl: canvas.toDataURL(),
              height: canvas.height,
              width: canvas.width,
            },
          });
        }
      }

      downloadActionPlanPPT({
        actionData: selectedActionPlan,
        actionGraphsImg,
        survey: currentSurvey,
        orgUnit: selectedOrgUnit,
        planActionStatus,
      });

      bodyCopy.style.width = '';
    }

    setSelectedActionPlanForPPT(null);
  };

  /**
   * Get the parent orgUnit of the selected orgUnit and fetch all child orgUnits.
   */
  useEffect(() => {
    if (selectedOrgUnit && planActionOrgTeeState) {
      // Get the parentOrgUnit of the selectedOrgUnit.
      const parentOrgUnit = getParentOrgUnit(
        selectedOrgUnit,
        planActionOrgTeeState.nodes,
      );

      // If the selectedOrgUnit is not equal to the parentOrgUnit, all children of the orgUnit are fetched.
      // If the parentOrgUnit is the same, the parentOrgUnit is used.
      if (selectedOrgUnit.org_unit_id !== parentOrgUnit.org_unit_id) {
        // Generate api path.
        const endpoint = generatePath(apiPaths.ORG_UNIT_CHILDREN, {
          id: parentOrgUnit.id,
        });

        // Get all childOrgUnits of the parentOrgUnit.
        HttpService.get(endpoint)
          .then((response) => {
            // Set the initialOrgUnits to those of the fetched orgUnits.
            setChildOrgUnits(response.data);

            return true;
          })
          .catch((error) => console.log(error));
      } else {
        setChildOrgUnits([parentOrgUnit]);
      }
    }
  }, [selectedOrgUnit]);

  if (selectedOrgUnit === null) return <></>;

  return (
    <>
      {loadingActionPlans ? (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={20} />
        </Stack>
      ) : (
        <Table size="small">
          <TableHead>
            <PlanActionTableHead />
          </TableHead>
          <TableBody>
            {actionPlans.length ? (
              actionPlans.map((actionPlan) => (
                <PlanActionTableRow
                  childOrgUnits={childOrgUnits}
                  key={actionPlan.id}
                  actionPlan={actionPlan}
                  // TODO: uncomment when i18n gets activated again
                  // onDownloadClick={setSelectedActionPlanForDownload}
                  onDownloadClick={() =>
                    actionPlan.id && downloadPPTActionPlan(actionPlan.id)
                  }
                />
              ))
            ) : (
              <TableRow>
                <td>{t('ActionPlan.NoActionPlanExists')}</td>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      <LanguageChooserModal
        onSelectLanguage={downloadPrintedActionPlan}
        languageChoices={[
          { key: 'en', translation: t('DownloadActionPlans.DownloadEnglish') },
          { key: 'de', translation: t('DownloadActionPlans.DownloadGerman') },
        ]}
        onClose={() => setSelectedActionPlanForDownload(null)}
        isOpen={selectedActionPlanForDownload !== null}
        headline={t('ActionPlan.DownloadActionPlan')}
      />
      {!!selectedActionPlanForPPT &&
        selectedActionPlanForPPT.actions.map((action) => (
          <div
            id={`kpi-graph-${action.id}`}
            key={action.id}
            style={{ fontSize: '45px', lineHeight: 'normal' }}
          >
            <div style={{ height: '350px' }}>
              {action.kpi && <KPIHistory kpi={action.kpi} bigKPI />}
            </div>

            {action.id && action.kpi && (
              <TargetStatementResults
                kpi={action.kpi}
                actionId={action.id}
                selectedTargetStatement={action.target_statement ?? undefined}
              />
            )}
          </div>
        ))}
      <PPTModal isOpen={!!selectedActionPlanForPPT} />
    </>
  );
};

export default PlanActionTable;
